import { useEffect, useRef, useState } from "react";
import { archiveStats, dossierStats, personality, skill, type StatItem } from "../../data/stats";
import { CartridgeHover, NoteHover } from "../CartridgeHover/CartridgeHover";
import { SectionShell } from "../layout/SectionShell";
import styles from "./Infographic.module.css";

function format(item: StatItem, n: number): string {
  if (item.numeric === undefined) return item.value;
  const rounded = Number.isInteger(item.numeric) ? Math.round(n) : Math.round(n * 100) / 100;
  const formatted = Number.isInteger(item.numeric)
    ? rounded.toLocaleString("en-US")
    : rounded.toFixed(2);
  return `${formatted}${item.suffix ?? ""}`;
}

function Count({ item, active }: { item: StatItem; active: boolean }) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!active || item.numeric === undefined) return;
    const target = item.numeric;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      const eased = 1 - (1 - t) ** 3;
      setN(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, item.numeric]);

  const inner =
    item.id === "caliber" ? (
      <CartridgeHover variant="stat" />
    ) : item.numeric === undefined ? (
      item.value
    ) : (
      format(item, n)
    );

  return (
    <div className={styles.row}>
      <span className={styles.label}>{item.label}</span>
      <span className={styles.value}>
        {item.note ? (
          <NoteHover variant="stat" title={item.label} body={item.note}>
            {inner}
          </NoteHover>
        ) : (
          inner
        )}
      </span>
    </div>
  );
}

function TileGrid() {
  return (
    <div className={styles.tiles} aria-label="Formation tiles">
      {skill.tiles.cells.map((cell, i) => (
        <span
          key={i}
          className={`${styles.cell} ${cell === "buff" ? styles.buff : ""} ${cell === "self" ? styles.self : ""}`}
          title={cell === "self" ? "Mondragón" : cell === "buff" ? "Affected tile" : undefined}
        />
      ))}
    </div>
  );
}

export function Infographic() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setInView(true);
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <SectionShell id="archive" kicker="Archive / Doll dossier" title="Mondragón’s Stat Sheet." alt>
      <div className={styles.grid} ref={ref}>
        <div className={`${styles.col} ${styles.archive}`}>
          <h3>Archive</h3>
          <div className={styles.list}>
            {archiveStats.map((item) => (
              <Count key={item.id} item={item} active={inView} />
            ))}
          </div>
        </div>
        <div className={styles.col}>
          <h3>Doll dossier</h3>
          <div className={styles.list}>
            {dossierStats.map((item) => (
              <Count key={item.id} item={item} active={inView} />
            ))}
          </div>
          <div className={styles.skill}>
            <h4>Skill 1 — {skill.name}</h4>
            <p className={styles.meta}>
              Initial {skill.initial}s · cooldown {skill.cooldownLv10}s at level 10. Values below are
              skill level 10.
            </p>
            <div className={styles.tileBlock}>
              <TileGrid />
              <div>
                <p className={styles.note}>{skill.tiles.affect}</p>
                <p className={styles.note}>{skill.tiles.bonus}</p>
              </div>
            </div>
            {skill.text.map((line) => (
              <p key={line.slice(0, 18)}>{line}</p>
            ))}
            <p className={styles.quote}>{personality}</p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
